import React, { useState } from 'react';
import { aiApi, itemsApi } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

interface AITestItemsButtonProps {
  onItemsGenerated?: () => void;
  className?: string;
}

export const AITestItemsButton: React.FC<AITestItemsButtonProps> = ({ 
  onItemsGenerated, 
  className = '' 
}) => {
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [count, setCount] = useState(5);
  const [lastResult, setLastResult] = useState<string>('');

  // Only show for admin users (check both role and accountRole)
  if (!user || (user.role !== 'admin' && user.accountRole !== 'admin' && user.accountRole !== 'owner')) {
    return null;
  }

  const handleGenerateItems = async () => {
    setIsGenerating(true);
    setLastResult('🔄 Generating AI items...');
    
    try {
      // Generate AI images
      console.log('Generating AI images...');
      const imagesResponse = await aiApi.generateImages(count);
      
      if (!imagesResponse.data.success || !imagesResponse.data.data?.images) {
        throw new Error('Failed to generate AI images');
      }

      const generatedImages = imagesResponse.data.data.images;
      console.log(`Generated ${generatedImages.length} AI images`);

      let successCount = 0;
      let failedCount = 0;

      // Process each image
      for (const imageUrl of generatedImages) {
        try {
          console.log('Processing AI image:', imageUrl);
          
          // Send OpenAI URL directly to backend for server-side processing
          const aiPayload = {
            imageUrl: imageUrl, // Send URL directly to backend
            title: '',
            description: '',
            condition: 'used' as const,
            materials: [],
            dimensions: {
              width: 0,
              height: 0,
              depth: 0,
              units: 'cm'
            }
          };
          
          console.log('🤖 Sending to AI API:', {
            hasImageUrl: !!aiPayload.imageUrl,
            imageUrl: aiPayload.imageUrl?.substring(0, 50) + '...'
          });
          
          const aiResponse = await aiApi.estimate(aiPayload);

          if (aiResponse.data.success && aiResponse.data.data) {
            const aiAnalysis = aiResponse.data.data;
            
            // Create item with AI-generated data using the original URL
            // The backend will handle converting external URLs to base64 when needed
            const itemData = {
              title: aiAnalysis.recommendedTitle || 'AI Generated Item',
              description: aiAnalysis.notes || 'AI generated construction item',
              images: [imageUrl], // Use the original OpenAI URL
              condition: 'used' as const,
              price: aiAnalysis.estimatedValue || 50,
              currency: 'NZD',
              materials: aiAnalysis.attributes || [],
              dimensions: {
                width: Math.floor(Math.random() * 100) + 50,
                height: Math.floor(Math.random() * 100) + 50,
                depth: Math.floor(Math.random() * 50) + 10,
                units: 'cm'
              },
              status: 'draft' as const,
              aiAnalysis: {
                estimatedValue: aiAnalysis.estimatedValue,
                confidence: aiAnalysis.confidence,
                notes: aiAnalysis.notes,
                recommendedTitle: aiAnalysis.recommendedTitle,
                attributes: aiAnalysis.attributes,
                reasoning: aiAnalysis.reasoning
              }
            };

            const createResponse = await itemsApi.create(itemData);
            
            if (createResponse.data.success) {
              successCount++;
              console.log('Successfully created AI item:', createResponse.data.data?.item?.title || itemData.title);
            } else {
              failedCount++;
              console.error('Failed to create item:', createResponse.data.error);
            }
          } else {
            failedCount++;
            console.error('AI analysis failed for image:', imageUrl);
          }
        } catch (error) {
          failedCount++;
          console.error('Error processing AI image:', error);
        }
      }

      // Show result message
      if (successCount > 0) {
        if (failedCount === 0) {
          setLastResult(`✅ Generated ${successCount} AI items successfully!`);
        } else {
          setLastResult(`⚠️ Generated ${successCount} items, ${failedCount} failed`);
        }
        setShowOptions(false);
        if (onItemsGenerated) {
          onItemsGenerated();
        }
      } else {
        setLastResult('❌ Failed to generate any AI items');
      }

    } catch (error: any) {
      console.error('Failed to generate AI items:', error);
      setLastResult(`❌ Error: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main Button */}
      <button
        onClick={() => setShowOptions(!showOptions)}
        disabled={isGenerating}
        className={`btn-primary p-2 ${
          isGenerating ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        title="AI Test"
      >
        {isGenerating ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        )}
      </button>

      {/* Options Panel */}
      {showOptions && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-slate-800 
                        rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 
                        p-4 z-50">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 dark:text-white">
                AI Test Items
              </h3>
              <button
                onClick={() => setShowOptions(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Count Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Number of Items to Generate
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 
                          rounded-md shadow-sm focus:outline-none focus:ring-2 
                          focus:ring-primary-500 focus:border-primary-500 
                          dark:bg-slate-700 dark:text-white"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Each item will be generated with AI images and analysis
              </p>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerateItems}
              disabled={isGenerating}
              className={`w-full btn-primary ${
                isGenerating ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isGenerating ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Generating Items...</span>
                </div>
              ) : (
                `Generate ${count} AI Item${count !== 1 ? 's' : ''}`
              )}
            </button>

            {/* Result Display */}
            {lastResult && (
              <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-md">
                <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {lastResult}
                </p>
              </div>
            )}

            {/* Info */}
            <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1">
              <p>• Generates AI images using OpenAI DALL-E</p>
              <p>• Analyzes each image for title, description, and pricing</p>
              <p>• Creates items with complete AI metadata</p>
              <p>• Items are created in draft status</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
